import { addUsersToOrganisation, removeUsersFromOrganisation } from '@infuse/api/iam/organisation';
import { addUsersToTenant, removeUsersFromTenant } from '@infuse/api/iam/tenant';
import { User, UserMoveDestinationType } from '@infuse/iam/user/type';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

interface MoveUsersData {
  users: User[];
  destinationType: UserMoveDestinationType;
  destinationId?: string; // undefined for domain
  targetTenantId?: string; // undefined for domain-level org, defined for tenant-level org
}

export const useMoveUsers = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<void, unknown, MoveUsersData>({
    mutationFn: async ({ users, destinationType, destinationId, targetTenantId }) => {
      try {
        // Process moves based on user's current location and destination
        for (const user of users) {
          await processSingleUserMove(user, destinationType, destinationId, targetTenantId);
        }

        notify({
          title: t('iam.users.header.move_users'),
          message: t('iam.users.notify.move_users_success', { count: users.length })
        });

        // Invalidate relevant queries to refresh user lists
        queryClient.invalidateQueries({ queryKey: ['users'] });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.users.header.move_users'),
          message: getAxiosErrorMessage(e, t('iam.users.notify.move_users_failed'))
        });
        throw e;
      }
    }
  });

  return {
    ...mutation
  };
};

/**
 * Determines the user's current location in the hierarchy and routes to the appropriate move handler.
 *
 * User hierarchy levels:
 * 1. Domain (no tenant, no org) - Top level
 * 2. Tenant (has tenant, no org) - Under domain
 * 3. Domain Organisation (no tenant, has org) - Organisation directly under domain
 * 4. Tenant Organisation (has tenant, has org) - Organisation under a tenant
 */
async function processSingleUserMove(
  user: User,
  destinationType: UserMoveDestinationType,
  destinationId?: string,
  targetTenantId?: string
) {
  const { tenantId: currentTenantId, organisationId: currentOrganisationId } = user;

  // Determine user's current level in the hierarchy
  const userLevel = {
    isDomain: !currentTenantId && !currentOrganisationId,
    isTenant: !!currentTenantId && !currentOrganisationId,
    isDomainOrganisation: !currentTenantId && !!currentOrganisationId,
    isTenantOrganisation: !!currentTenantId && !!currentOrganisationId
  };

  // Route to appropriate handler based on current level
  if (userLevel.isDomain) {
    await moveDomainUser(user.id, destinationType, destinationId);
  } else if (userLevel.isTenant) {
    //@ts-expect-error @typescript-eslint/no-non-null-assertion
    await moveTenantUser(user.id, currentTenantId!, destinationType, destinationId);
  } else if (userLevel.isDomainOrganisation) {
    //@ts-expect-error @typescript-eslint/no-non-null-assertion
    await moveDomainOrganisationUser(user.id, currentOrganisationId!, destinationType, destinationId);
  } else if (userLevel.isTenantOrganisation) {
    await moveTenantOrganisationUser(
      user.id,
      //@ts-expect-error @typescript-eslint/no-non-null-assertion
      currentTenantId!,
      //@ts-expect-error @typescript-eslint/no-non-null-assertion
      currentOrganisationId!,
      destinationType,
      destinationId,
      targetTenantId
    );
  }
}

/**
 * Moves a user from Domain level.
 * Possible moves:
 * - Domain -> Tenant (add to tenant)
 * - Domain -> Organisation (add to org)
 */
async function moveDomainUser(userId: string, destinationType: UserMoveDestinationType, destinationId?: string) {
  if (destinationType === UserMoveDestinationType.DOMAIN) {
    return; // Already at domain level - no action needed
  }

  if (destinationType === UserMoveDestinationType.TENANT && destinationId) {
    await addUsersToTenant({ id: destinationId }, { userIds: [userId] });
  } else if (destinationType === UserMoveDestinationType.ORGANISATION && destinationId) {
    await addUsersToOrganisation({ id: destinationId }, { userIds: [userId] });
  }
}

/**
 * Moves a user from Tenant level (user is in a tenant, but not in an organisation).
 * Possible moves:
 * - Tenant -> Domain (remove from tenant)
 * - Tenant -> Another Tenant (remove from current, add to new)
 * - Tenant -> Organisation (remove from tenant, add to org)
 */
async function moveTenantUser(
  userId: string,
  currentTenantId: string,
  destinationType: UserMoveDestinationType,
  destinationId?: string
) {
  // Moving back to domain: just remove from tenant
  if (destinationType === UserMoveDestinationType.DOMAIN) {
    await removeUsersFromTenant({ id: currentTenantId }, { userIds: [userId] });
    return;
  }

  // Moving to organisation: remove from tenant, add to org
  if (destinationType === UserMoveDestinationType.ORGANISATION && destinationId) {
    await removeUsersFromTenant({ id: currentTenantId }, { userIds: [userId] });
    await addUsersToOrganisation({ id: destinationId }, { userIds: [userId] });
    return;
  }

  // Moving to different tenant: remove from current, add to new
  if (destinationType === UserMoveDestinationType.TENANT && destinationId && destinationId !== currentTenantId) {
    await removeUsersFromTenant({ id: currentTenantId }, { userIds: [userId] });
    await addUsersToTenant({ id: destinationId }, { userIds: [userId] });
  }
}

/**
 * Moves a user from Tenant Organisation level (user is in an organisation within a tenant).
 * Most complex case as organisations can be in different tenants or at domain level.
 *
 * Possible moves:
 * - Tenant Org -> Domain (remove from tenant, which cascades org removal)
 * - Tenant Org -> Tenant in same tenant (remove from org only, stay in tenant)
 * - Tenant Org -> Tenant in different tenant (remove from current tenant, add to new tenant)
 * - Tenant Org -> Org in same tenant (remove from current org, add to new org, stay in tenant)
 * - Tenant Org -> Org in different tenant (remove from current tenant, add to new org+tenant)
 * - Tenant Org -> Org at domain level (remove from tenant, add to domain org)
 */
async function moveTenantOrganisationUser(
  userId: string,
  currentTenantId: string,
  currentOrganisationId: string,
  destinationType: UserMoveDestinationType,
  destinationId?: string,
  targetTenantId?: string // For org moves: which tenant the target org is in (undefined = domain org)
) {
  // Moving to domain: remove from tenant (cascade removes from org)
  if (destinationType === UserMoveDestinationType.DOMAIN) {
    await removeUsersFromTenant({ id: currentTenantId }, { userIds: [userId] });
    return;
  }

  // Moving to a tenant
  if (destinationType === UserMoveDestinationType.TENANT && destinationId) {
    const movingToSameTenant = destinationId === currentTenantId;

    if (movingToSameTenant) {
      // Move up one level: remove from org, stay in tenant
      await removeUsersFromOrganisation({ id: currentOrganisationId }, { userIds: [userId] });
    } else {
      // Move to different tenant: remove from current tenant, add to new tenant
      await removeUsersFromTenant({ id: currentTenantId }, { userIds: [userId] });
      await addUsersToTenant({ id: destinationId }, { userIds: [userId] });
    }
    return;
  }

  // Moving to an organisation
  if (
    destinationType === UserMoveDestinationType.ORGANISATION &&
    destinationId &&
    destinationId !== currentOrganisationId
  ) {
    const isDomainOrg = targetTenantId === undefined;
    const isSameTenantOrg = targetTenantId === currentTenantId;

    if (isDomainOrg) {
      // Moving to domain-level org: remove from tenant (cascade removes from current org), add to domain org
      await removeUsersFromTenant({ id: currentTenantId }, { userIds: [userId] });
      await addUsersToOrganisation({ id: destinationId }, { userIds: [userId] });
    } else if (isSameTenantOrg) {
      // Moving to org in same tenant: just switch orgs, stay in tenant
      await removeUsersFromOrganisation({ id: currentOrganisationId }, { userIds: [userId] });
      await addUsersToOrganisation({ id: destinationId }, { userIds: [userId] });
    } else {
      // Moving to org in different tenant: remove from current tenant, add to new org (which adds to its tenant)
      await removeUsersFromTenant({ id: currentTenantId }, { userIds: [userId] });
      await addUsersToOrganisation({ id: destinationId }, { userIds: [userId] });
    }
  }
}

/**
 * Moves a user from Domain Organisation level (user is in an organisation at domain level, not in a tenant).
 * Possible moves:
 * - Domain Org -> Domain (remove from org)
 * - Domain Org -> Tenant (remove from org, add to tenant)
 * - Domain Org -> Another Org (remove from current org, add to new org)
 */
async function moveDomainOrganisationUser(
  userId: string,
  currentOrganisationId: string,
  destinationType: UserMoveDestinationType,
  destinationId?: string
) {
  // Moving to domain: just remove from org
  if (destinationType === UserMoveDestinationType.DOMAIN) {
    await removeUsersFromOrganisation({ id: currentOrganisationId }, { userIds: [userId] });
    return;
  }

  // Moving to tenant: remove from org, add to tenant
  if (destinationType === UserMoveDestinationType.TENANT && destinationId) {
    await removeUsersFromOrganisation({ id: currentOrganisationId }, { userIds: [userId] });
    await addUsersToTenant({ id: destinationId }, { userIds: [userId] });
    return;
  }

  // Moving to different org: remove from current, add to new
  if (
    destinationType === UserMoveDestinationType.ORGANISATION &&
    destinationId &&
    destinationId !== currentOrganisationId
  ) {
    await removeUsersFromOrganisation({ id: currentOrganisationId }, { userIds: [userId] });
    await addUsersToOrganisation({ id: destinationId }, { userIds: [userId] });
  }
}
